import { item, ITEMS } from "./data/items.ts";
import { bird } from "./data/story.ts";
import {
  STAT_KEYS,
  type Archetype,
  type BirdLook,
  type Gear,
  type Hero,
  type Item,
  type Opponent,
  type SpecialId,
  type Stats,
  type WeaponItem,
} from "./types.ts";

export const BASE_STAT = 3;
export const START_POINTS = 15;
export const POINTS_PER_LEVEL = 5;
export const START_GOLD = 60;

export const STAT_INFO: Record<keyof Stats, { name: string; desc: string }> = {
  str: { name: "Strength", desc: "Raw pecking power. More damage per hit." },
  atk: { name: "Attack", desc: "Weapon skill. Better chance to land hits." },
  def: { name: "Defence", desc: "Guard and toughness. Harder to hit; better blocks." },
  agi: { name: "Agility", desc: "Fancy footwork. Dodge blows and cover more ground." },
  vit: { name: "Vitality", desc: "Health. How much plucking you can survive." },
  sta: { name: "Stamina", desc: "Energy for attacks and Fowl Arts." },
  cha: { name: "Charisma", desc: "Crowd appeal. Taunts, critical hits and bigger purses." },
};

export const SPECIAL_INFO: Record<SpecialId, { name: string; desc: string; energy: number }> = {
  egg: { name: "Egg Grenade", desc: "Lob an egg at any range. Ignores shields. 2 per fight.", energy: 12 },
  roar: { name: "Rooster Roar", desc: "Terrifying battle cry. May stun the foe for a turn.", energy: 14 },
  frenzy: { name: "Feather Frenzy", desc: "Three rapid strikes in one turn. Melee only.", energy: 22 },
  wind: { name: "Second Wind", desc: "Restore 35% of max health. Once per fight.", energy: 0 },
};

export const FISTS: WeaponItem = {
  id: "beak",
  slot: "weapon",
  name: "Bare Beak",
  shape: "stick",
  material: "wood",
  tier: 0,
  price: 0,
  min: 2,
  max: 4,
  acc: 5,
  reach: 1,
  twoHanded: false,
  desc: "Nature's weapon.",
};

export function emptyStats(v = 0): Stats {
  return { str: v, atk: v, def: v, agi: v, vit: v, sta: v, cha: v };
}

export function gearItems(gear: Gear): Item[] {
  return Object.values(gear)
    .filter((id): id is string => !!id)
    .map(item);
}

export function effectiveStats(base: Stats, gear: Gear): Stats {
  const s = { ...base };
  for (const it of gearItems(gear)) {
    for (const k of STAT_KEYS) s[k] += it.bonus?.[k] ?? 0;
  }
  for (const k of STAT_KEYS) s[k] = Math.max(1, s[k]);
  return s;
}

export interface Derived {
  stats: Stats;
  maxHp: number;
  maxEn: number;
  armor: number;
  block: number;
  weapon: WeaponItem;
}

export function derive(base: Stats, gear: Gear): Derived {
  const stats = effectiveStats(base, gear);
  let armor = 0;
  let block = 0;
  let weapon = FISTS;
  for (const it of gearItems(gear)) {
    if (it.slot === "weapon") weapon = it;
    else armor += it.armor;
    if (it.slot === "shield") block = Math.min(45, it.block + stats.def * 0.35);
  }
  return { stats, maxHp: 20 + stats.vit * 6, maxEn: 30 + stats.sta * 4, armor, block, weapon };
}

export function xpToNext(level: number): number {
  return 40 + level * 60;
}

export function xpReward(level: number, won: boolean): number {
  const xp = 40 + level * 35;
  return won ? xp : Math.round(xp * 0.3);
}

export function goldReward(level: number, crowd: number, boss: boolean): number {
  return Math.round((20 + level * 18) * (boss ? 2 : 1) * (1 + crowd / 200));
}

/** Applies XP, returning the number of levels gained. */
export function gainXp(hero: Hero, xp: number): number {
  hero.xp += xp;
  let gained = 0;
  while (hero.xp >= xpToNext(hero.level)) {
    hero.xp -= xpToNext(hero.level);
    hero.level++;
    hero.points += POINTS_PER_LEVEL;
    gained++;
  }
  return gained;
}

export function meetsReq(hero: Hero, it: Item): boolean {
  if (!it.req) return true;
  return STAT_KEYS.every((k) => hero.base[k] >= (it.req?.[k] ?? 0));
}

export function sellPrice(it: Item): number {
  return Math.floor(it.price / 2);
}

/** Equip an item, unequipping anything it conflicts with (two-handers vs shields). */
export function equip(hero: Hero, id: string): void {
  const it = item(id);
  hero.gear[it.slot] = id;
  if (it.slot === "weapon" && it.twoHanded) delete hero.gear.shield;
  if (it.slot === "shield") {
    const w = hero.gear.weapon ? item(hero.gear.weapon) : null;
    if (w?.slot === "weapon" && w.twoHanded) delete hero.gear.weapon;
  }
}

const WEIGHTS: Record<Archetype, Stats> = {
  brute: { str: 3, atk: 2, def: 1, agi: 1, vit: 2.6, sta: 1.4, cha: 0.5 },
  duelist: { str: 2, atk: 2.6, def: 1, agi: 2.4, vit: 1.8, sta: 1.4, cha: 0.8 },
  tank: { str: 2, atk: 1.7, def: 2.8, agi: 0.6, vit: 3, sta: 1.3, cha: 0.6 },
  showman: { str: 2, atk: 2.1, def: 1.2, agi: 1.5, vit: 2, sta: 1.3, cha: 2.4 },
  balanced: { str: 2.3, atk: 2.2, def: 1.6, agi: 1.3, vit: 2.3, sta: 1.4, cha: 0.9 },
};

export function opponentStats(o: Opponent): Stats {
  // Opponents get 5 fewer points than a hero of the same level; gear and Fowl Arts carry them.
  const points = (START_POINTS - 5 + (o.level - 1) * POINTS_PER_LEVEL) * (o.power ?? 1);
  const w = WEIGHTS[o.archetype];
  const total = STAT_KEYS.reduce((a, k) => a + w[k], 0);
  const s = emptyStats(BASE_STAT);
  for (const k of STAT_KEYS) s[k] += Math.round((points * w[k]) / total);
  return s;
}

export function newHero(name: string, look: BirdLook, base: Stats): Hero {
  return {
    name,
    look,
    base,
    level: 1,
    xp: 0,
    points: 0,
    gold: START_GOLD,
    inventory: ["twig"],
    gear: { weapon: "twig" },
    specials: [],
    progress: 0,
    wins: 0,
    losses: 0,
    seenScenes: [],
    finished: false,
  };
}

// ---------- Exhibition opponents ----------

const NAMES = [
  "Clucky Balboa", "Attila the Hen", "Eggward Scissorhands", "Cluck Norris", "Genghis Hen", "Vlad the Impeckler",
  "Mother Clucker", "Chick Jagger", "Beakonce", "Poultrygeist", "Nugget Ninja", "Drumstick Dan", "Sir Lancelittle",
  "Henrietta the Terror", "The Eggsecutioner", "Feather Locklear", "Rooster Cogburn", "Wingston Churchill",
  "Albert Eggstein", "Hen Diesel", "Pecky Blinders", "Yolko Ono", "Scrambled Sam", "Eggs Benedict Arnold",
  "Henny Penny", "Captain Peck Sparrow", "Omelette du Fromage", "Fowl Mouth Fred", "Brock Lesnest", "Marie Antoinegg",
];
const TITLES = ["Arena Brawler", "Pit Fighter", "Coliseum Regular", "Wandering Duelist", "Crowd Favorite", "Grizzled Veteran", "Rising Star", "Undefeated (Allegedly)"];
const TAUNTS = [
  "I've cracked tougher eggs than you!",
  "You call that a comb? I call it a target!",
  "Nothing personal. The crowd paid to see feathers fly.",
  "My mama raised me in a henhouse and I raise heck in the arena!",
  "When I'm done with you, they'll call you extra crispy.",
  "Bawk bawk BAWK! (That was a threat.)",
  "I'm the reason the chicken crossed the road. To get away from me.",
];
const PLUMAGE = ["#f5f1e6", "#f2e2b8", "#e8b04a", "#a64b2a", "#7a4a2a", "#2b2b33", "#7d8a99", "#c9853a", "#b9a6d6", "#9b2d20", "#5b3a29", "#e9d36c"];
const ARCHETYPES: Archetype[] = ["brute", "duelist", "tank", "showman", "balanced"];

function pick<T>(a: readonly T[], rand: () => number): T {
  return a[Math.floor(rand() * a.length)] as T;
}

function randomLook(rand: () => number): BirdLook {
  const roll = rand();
  if (roll < 0.1) {
    const body = pick(["#f4f4ee", "#8d8d86"], rand);
    return bird({ body, wing: body, tail: "#3a3a3a", beak: "#f08a24", legs: "#f08a24", combStyle: "none", wattle: false, beakStyle: "duck", tailStyle: "stub", neck: 1.8, eyes: "fierce" });
  }
  if (roll < 0.2) {
    return bird({ body: "#4a3426", wing: "#6b4a32", tail: "#5c3c26", head: "#d97a7a", combStyle: "snood", beak: "#c9b48a", legs: "#c96a5a", tailStyle: "fan", pattern: "barred", patternColor: "#2a1c14", size: 1.2 });
  }
  const body = pick(PLUMAGE, rand);
  return bird({
    body,
    wing: pick(PLUMAGE, rand),
    tail: pick(PLUMAGE, rand),
    combStyle: pick(["single", "rose", "crest", "mohawk"] as const, rand),
    tailStyle: pick(["fluffy", "sickle", "fan"] as const, rand),
    pattern: pick(["solid", "solid", "speckled", "barred", "laced"] as const, rand),
    patternColor: pick(["#2b2b33", "#f5f1e6", "#7a4a2a"], rand),
    eyes: pick(["normal", "fierce", "fierce", "sleepy", "shades"] as const, rand),
    extra: pick(["none", "none", "eyepatch", "scar", "bandana", "monocle", "mustache"] as const, rand),
    extraColor: pick(["#d32f2f", "#2f6fd6", "#2e7d32", "#111111"], rand),
    size: 0.9 + rand() * 0.3,
  });
}

function randomGear(level: number, rand: () => number): Gear {
  const maxTier = Math.min(6, 1 + Math.floor(level / 3.5));
  const gear: Gear = {};
  for (const slot of ["weapon", "armor", "helmet", "boots", "shield"] as const) {
    if (slot !== "weapon" && rand() < 0.3) continue;
    const pool = ITEMS.filter((i) => i.slot === slot && i.tier <= maxTier && i.tier >= maxTier - 1);
    if (!pool.length) continue;
    const it = pick(pool, rand);
    if (slot === "shield" && gear.weapon && item(gear.weapon).slot === "weapon" && (item(gear.weapon) as WeaponItem).twoHanded) continue;
    gear[slot] = it.id;
  }
  return gear;
}

export function exhibitionOpponent(heroLevel: number, rand: () => number = Math.random): Opponent {
  const level = Math.max(1, heroLevel + pick([-1, 0, 0, 0, 1], rand));
  return {
    id: "exhibition",
    name: pick(NAMES, rand),
    title: pick(TITLES, rand),
    level,
    archetype: pick(ARCHETYPES, rand),
    look: randomLook(rand),
    gear: randomGear(level, rand),
    taunt: pick(TAUNTS, rand),
  };
}

/** Fowl Arts an opponent knows: one per four levels, plus one for bosses. */
export function opponentSpecials(o: Opponent): SpecialId[] {
  const all: SpecialId[] = ["egg", "roar", "frenzy", "wind"];
  return all.slice(0, Math.floor((o.level - 1) / 4) + (o.boss ? 1 : 0));
}
